import { useState, useEffect } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import clsx from 'clsx';
import { GenericTabsProps } from './types';

export const GenericTabs = <T = string,>({ options, defaultSelected, onChange, isLoading = false, disabled = false, className, id }: GenericTabsProps<T>) => {
  const [selected, setSelected] = useState(defaultSelected || options[0]?.value || '');

  useEffect(() => {
    if (defaultSelected) {
      setSelected(defaultSelected);
    }
  }, [defaultSelected]);

  const handleTabClick = (value: string) => {
    setSelected(value);
    onChange(value as T);
  };

  // Render count badge - handles loading, null (stale), and numeric states
  const renderCountBadge = (count: number | null | undefined, isSelected: boolean) => {
    // Global loading state - show spinner
    if (isLoading) {
      return (
        <span className='flex items-center justify-center w-[24px] h-[20px]'>
          <CircularProgress size={12} thickness={4} sx={{ color: '#808080' }} />
        </span>
      );
    }

    // null = stale/loading state for this specific tab - show spinner
    if (count === null) {
      return (
        <span className='flex items-center justify-center w-[24px] h-[20px]'>
          <CircularProgress size={12} thickness={4} sx={{ color: '#808080' }} />
        </span>
      );
    }

    // undefined = no count to show
    if (count === undefined) {
      return null;
    }

    // Normal numeric count
    return (
      <span
        className={clsx(
          'text-12 rounded-full px-6 py-1 font-medium transition-all duration-300',
          isSelected ? 'bg-primary text-white' : 'bg-neutral-20 text-neutral-50 group-hover:bg-neutral-40 group-hover:text-neutral-70'
        )}
      >
        {count}
      </span>
    );
  };

  return (
    <div
      id={id}
      className={clsx('flex items-center gap-8 bg-[#F3F3F3] rounded-lg border border-neutral-20 p-4 w-[20rem] xl:w-[31.25rem] justify-center', className)}
    >
      {options.map((option) => (
        <button
          key={`tab-${option.value}`}
          type='button'
          onClick={() => handleTabClick(option.value)}
          disabled={disabled || option.disabled}
          className={clsx(
            'px-10 xl:px-16 py-8 rounded-md text-12 xl:text-14 font-medium transition-all group flex items-center gap-6 xl:gap-10 text-neutral-60 flex-1 justify-center',
            selected === option.value ? 'bg-white text-neutral-90 shadow-sm' : 'bg-transparent text-neutral-60 hover:bg-neutral-20 hover:text-neutral-60',
            (disabled || option.disabled) && 'opacity-50 cursor-not-allowed'
          )}
        >
          {typeof option.label === 'string' ? <span>{option.label}</span> : option.label}
          {renderCountBadge(option.count, selected === option.value)}
          {/* Render optional content after count */}
          {option?.afterCount}
        </button>
      ))}
    </div>
  );
};
